import type * as React from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * The inline-edit ROW family: a hover-washed row whose value cell flips between
 * a read trigger and an editing cell. `EditableValueCell` (sibling file)
 * orchestrates these parts; row hosts also compose them directly.
 *
 * `InlineRow` and `InlineValueCell` each host an absolutely-positioned child,
 * so both declare `relative` explicitly — Box is `position: static`.
 */

// No fill: the row is a region of whatever it sits on, and the wash below
// is what makes it read as a row.
//
// The row's current wash is exported as `--row-wash` — the same variable a
// DataTable row maintains — so a descendant that must paint an opaque copy
// of the row's composited color (the value-actions pill) can rebuild it
// without knowing which host it is in. The surface layer below paints FROM
// the variable, so the two can never disagree.
const INLINE_ROW_CLASS =
  "group/inline-row relative overflow-visible [--row-wash:transparent]";

const INLINE_ROW_WASH_HOVER_CLASS =
  "hover:[--row-wash:var(--wash-selected)]";

const INLINE_ROW_WASH_ON_CLASS = "[--row-wash:var(--wash-selected)]";

const INLINE_ROW_SURFACE_CLASS =
  "pointer-events-none absolute -inset-x-2 -inset-y-px rounded-sm bg-(--row-wash)";

const INLINE_ROW_CONTENT_CLASS = "relative z-10";

const INLINE_ROW_ACTIONS_HOVER_FOCUS_CLASS =
  "opacity-0 pointer-events-none group-hover/inline-row:opacity-100 group-hover/inline-row:pointer-events-auto group-has-focus-visible/inline-row:opacity-100 group-has-focus-visible/inline-row:pointer-events-auto";

const INLINE_ROW_ACTIONS_HOVER_CLASS =
  "opacity-0 pointer-events-none group-hover/inline-row:opacity-100 group-hover/inline-row:pointer-events-auto";

const INLINE_ROW_ACTIONS_VALUE_HOVER_FOCUS_CLASS =
  "opacity-0 pointer-events-none group-hover/inline-value-cell:opacity-100 group-hover/inline-value-cell:pointer-events-auto group-has-focus-visible/inline-value-cell:opacity-100 group-has-focus-visible/inline-value-cell:pointer-events-auto";

const INLINE_ROW_ACTIONS_VISIBLE_CLASS = "opacity-100 pointer-events-auto";

const INLINE_ROW_ACTIONS_HIDDEN_CLASS = "opacity-0 pointer-events-none";

// The focus treatment is `has-focus-visible`, not `focus-within`. A read-state
// cell whose trigger opens an overlay (a select, a date picker, a member picker)
// keeps that trigger mounted, and closing the overlay hands focus back to it —
// under `focus-within` the cell then stays lit and its actions stay revealed
// with the pointer nowhere near it. A text cell never showed this because
// committing unmounts the input. `:focus-visible` is exactly the distinction:
// a mouse-clicked button does not match it, a focused text input and a
// keyboard-reached trigger both do.
// Like the row above, the cell's own wash travels as `--cell-wash` and the
// background paints from it — the value-actions pill stacks this layer over
// `--row-wash` to reproduce the cell's exact composited color.
const INLINE_VALUE_CELL_CLASS =
  "group/inline-value-cell flex-1 min-w-0 w-full h-full self-stretch flex-row items-center overflow-hidden rounded-sm border border-transparent transition-[background-color,border-color] [--cell-wash:transparent] bg-(--cell-wash) hover:border-border/60 hover:[--cell-wash:var(--wash-hover)] has-focus-visible:border-border/60 has-focus-visible:[--cell-wash:var(--wash-hover)]";

const INLINE_VALUE_CELL_BLEED_END_CLASS = "min-w-[calc(100%+0.5rem)] pr-2";

const INLINE_VALUE_TRIGGER_CLASS =
  "flex-1 min-w-0 h-full self-stretch flex-row items-center overflow-hidden rounded-none border-0 bg-transparent focus-visible:outline-none";

const INLINE_EDITING_CELL_CLASS =
  "flex-1 min-w-0 w-full h-full self-stretch overflow-hidden rounded-sm border border-primary [--cell-wash:var(--wash-active)] bg-(--cell-wash) focus-within:border-primary";

const INLINE_EDITING_CELL_BLEED_END_CLASS = "min-w-[calc(100%+0.5rem)] pr-2";

export type InlineRowProps = Omit<
  BoxProps<"div">,
  "border" | "borderColor" | "className"
> & Omittable<{
  highlighted?: boolean;
  beforeHighlighted?: boolean;
}>;

export function InlineRow({
  highlighted = false,
  beforeHighlighted = false,
  children,
  ...props
}: InlineRowProps): React.ReactElement {
  const suppressDivider = highlighted || beforeHighlighted;

  return (
    <Box
      direction="row"
      border="bottom"
      borderColor="border"
      className={cn(
        INLINE_ROW_CLASS,
        highlighted ? INLINE_ROW_WASH_ON_CLASS : INLINE_ROW_WASH_HOVER_CLASS,
        suppressDivider && "border-transparent",
      )}
      {...props}
    >
      <Box className={INLINE_ROW_SURFACE_CLASS} />
      {children}
    </Box>
  );
}

export type InlineRowContentProps = Omit<BoxProps<"div">, "className">;

export function InlineRowContent({
  children,
  ...props
}: InlineRowContentProps): React.ReactElement {
  return (
    <Box className={INLINE_ROW_CONTENT_CLASS} {...props}>
      {children}
    </Box>
  );
}

export type InlineHeaderRowProps = Omit<
  BoxProps<"div">,
  "border" | "borderColor" | "className"
> & Omittable<{
  suppressDivider?: boolean;
}>;

export function InlineHeaderRow({
  suppressDivider = false,
  children,
  ...props
}: InlineHeaderRowProps): React.ReactElement {
  return (
    <Box
      direction="row"
      border="bottom"
      borderColor="border"
      className={cn(suppressDivider && "border-transparent")}
      {...props}
    >
      {children}
    </Box>
  );
}

export type InlineRowActionsVisibility =
  | "hoverFocus"
  | "hover"
  | "valueHoverFocus"
  | "always"
  | "hidden";

const INLINE_ROW_ACTIONS_VISIBILITY_CLASSES: Record<
  InlineRowActionsVisibility,
  string
> = {
  hoverFocus: INLINE_ROW_ACTIONS_HOVER_FOCUS_CLASS,
  hover: INLINE_ROW_ACTIONS_HOVER_CLASS,
  valueHoverFocus: INLINE_ROW_ACTIONS_VALUE_HOVER_FOCUS_CLASS,
  always: INLINE_ROW_ACTIONS_VISIBLE_CLASS,
  hidden: INLINE_ROW_ACTIONS_HIDDEN_CLASS,
};

export type InlineRowActionsProps = BoxProps<"div"> & Omittable<{
  visibility?: InlineRowActionsVisibility;
}>;

export function InlineRowActions({
  visibility = "hoverFocus",
  className,
  children,
  ...props
}: InlineRowActionsProps): React.ReactElement {
  return (
    <Box
      direction="row"
      align="center"
      justify="center"
      gap="xs"
      className={cn(
        INLINE_ROW_ACTIONS_VISIBILITY_CLASSES[visibility],
        className,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

export type InlineValueCellProps = Omit<BoxProps<"div">, "className"> & Omittable<{
  bleedEnd?: boolean;
}>;

export function InlineValueCell({
  bleedEnd = false,
  children,
  ...props
}: InlineValueCellProps): React.ReactElement {
  return (
    <Box
      direction="row"
      align="center"
      position="relative"
      className={cn(
        INLINE_VALUE_CELL_CLASS,
        bleedEnd && INLINE_VALUE_CELL_BLEED_END_CLASS,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

export type InlineValueTriggerProps = Omit<BoxProps<"button">, "as">;

/** The read-state trigger inside a value cell — a real button element. */
export function InlineValueTrigger({
  children,
  disabled,
  className,
  ...props
}: InlineValueTriggerProps): React.ReactElement {
  return (
    <Box<"button">
      as="button"
      disabled={disabled}
      {...props}
      className={cn(INLINE_VALUE_TRIGGER_CLASS, className)}
    >
      {children}
    </Box>
  );
}

export type InlineEditingCellProps = Omit<BoxProps<"div">, "className"> & Omittable<{
  bleedEnd?: boolean;
}>;

export function InlineEditingCell({
  bleedEnd = false,
  children,
  ...props
}: InlineEditingCellProps): React.ReactElement {
  return (
    // A row that centers, like the read cell beside it. `fill="flex"` claims the
    // free space but sets no display, so without these the cell is a block and a
    // control shorter than the row height sits against its top edge.
    <Box
      direction="row"
      align="center"
      fill="flex"
      className={cn(
        INLINE_EDITING_CELL_CLASS,
        bleedEnd && INLINE_EDITING_CELL_BLEED_END_CLASS,
      )}
      {...props}
    >
      {children}
    </Box>
  );
}
